import React, { useState } from 'react';
import type { CSSProperties, PointerEvent as PointerEventReact } from 'react';
import { createPortal } from 'react-dom';
import { Trans, useTranslation } from 'react-i18next';
import {
  Link,
  useLocation,
  // Sin el `useNavigate` propio: su «volver si es la anterior» rompe la
  // regla de pestañas (ver `ui/pestanas`).
  // oxlint-disable-next-line eslint/no-restricted-imports
  useNavigate,
} from 'react-router';

import { useResponsive } from '@actual-app/components/hooks/useResponsive';
import { styles } from '@actual-app/components/styles';
import { css } from '@emotion/css';

import {
  DIAMETRO_BRUJULA,
  MenuOpciones,
} from '#components/mobile/opciones/MenuOpciones';
import { useNumeroPorRevisar } from '#components/mobile/revisar/usePorRevisar';
import { useTravesia } from '#components/mobile/travesia/TravesiaContext';

import { barraVisibleEn, pestanaActiva } from './opciones/opciones';
import type { Pestana } from './opciones/opciones';
import { Icono } from './ui/Icono';
import type { NombreIcono } from './ui/Icono';
import { irAPestana, RUTA_INICIO } from './ui/pestanas';
import { color, movimiento, radio, sombra } from './ui/tokens';

// Barra de pestañas fija (no se esconde al desplazar): Movimientos · Plan ·
// Inicio (grande, en el centro) · Cuentas · Informes y, suelta a la derecha,
// la brújula «Opciones» (botón redondo y verde, no es una pestaña), que
// despliega las opciones de la pantalla actual (ver `opciones/`).
const ALTO_BARRA = 62;
const MARGEN = 10;
/** Diámetro de Inicio y cuánto sobresale por encima de la barra. */
const INICIO = 64;
const SOBRESALE = 18;

/** Hueco que deben reservar las páginas al final para no quedar tapadas. */
export const MOBILE_NAV_HEIGHT = ALTO_BARRA + MARGEN * 2 + SOBRESALE;

/** Hueco entre la barra y la brújula. */
const HUECO_BRUJULA = 8;

/** Distancia del borde inferior a la que empiezan las píldoras de opciones. */
const ENCIMA_DE_LA_BARRA = `calc(${ALTO_BARRA + MARGEN + 12}px + env(safe-area-inset-bottom, 0px))`;

/** Opacidad de las pestañas apagadas durante la travesía (maqueta: 0,5). */
const OPACIDAD_TRAVESIA = 0.5;

/**
 * Barra atenuada mientras la brújula está abierta (B5). Con filtro y no con
 * opacidad: con opacidad se transparentaba y se veía la lista de detrás.
 */
export const FILTRO_BARRA_CON_MENU = 'saturate(0.35) brightness(0.88)';

/**
 * ¿Un toque en la barra (B5) debe cerrar la brújula? Sí si cae dentro de la
 * propia barra y no en el botón de la brújula (ese ya abre y cierra solo).
 * Las píldoras salen en un portal fuera de la barra: sus toques no cuentan,
 * aunque React los propague por el árbol.
 * Tampoco cierra al pulsar una pestaña distinta de la actual: esa navega (con
 * `replace` sobre la entrada de historial de la brújula) y la brújula se cierra
 * sola al cambiar de ruta. Cerrarla antes lanzaría un `history.back()` que, si
 * llega después de la navegación, devolvería a la pantalla anterior.
 */
export function toqueCierraMenu(
  barra: Element,
  destino: EventTarget | null,
): boolean {
  if (!(destino instanceof Node) || !barra.contains(destino)) {
    return false;
  }
  const elemento = destino instanceof Element ? destino : destino.parentElement;
  if (!elemento || elemento.closest('[data-testid="brujula-opciones"]')) {
    return false;
  }
  return !elemento.closest('a[href]:not([aria-current])');
}

type DatosPestana = {
  id: Exclude<Pestana, 'inicio'>;
  nombre: string;
  ruta: string;
  icono: NombreIcono;
};

function estiloPestana(activa: boolean): CSSProperties {
  return {
    ...styles.noTapHighlight,
    height: ALTO_BARRA - 10,
    minWidth: 0,
    padding: 0,
    borderRadius: 16,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
    textDecoration: 'none',
    userSelect: 'none',
    fontSize: 11,
    fontWeight: activa ? 800 : 700,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    color: activa ? color.accent : color.fg3,
    backgroundColor: activa ? color.accentSoft : 'transparent',
    // Sin transición (H15): al cambiar rápido de pestaña, el fondo de la
    // anterior se apagaba despacio y se veían dos pestañas iluminadas.
    transition: 'none',
  };
}

/**
 * Mientras se personaliza Inicio, la barra baja y se esconde: así se ven
 * más tarjetas y no se cambia de pestaña a medio arrastrar.
 */
export const ATRIBUTO_SIN_BARRA = 'data-editando-inicio';
const claseBarra = css({
  transition: 'transform 300ms ease, opacity 300ms',
  [`body[${ATRIBUTO_SIN_BARRA}] &`]: {
    transform: 'translateY(140%)',
    opacity: 0,
    pointerEvents: 'none',
  },
  '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
});

export function MobileNavTabs() {
  const { t } = useTranslation();
  const { isNarrowWidth } = useResponsive();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const nuevos = useNumeroPorRevisar();
  // Estado de la brújula aquí (B5): la barra se atenúa mientras está abierta
  // y tocarla la cierra (tocar otra pestaña además navega).
  const [menuAbierto, setMenuAbierto] = useState(false);
  // Primera travesía: barra atenuada y sin navegar, con la pestaña del paso
  // encendida. Sin travesía (`null`) todo sigue igual.
  const travesia = useTravesia();
  const bloqueada = !!travesia?.activa;
  const brujulaForzada = !!travesia?.brujulaForzada;

  if (!isNarrowWidth || !barraVisibleEn(pathname)) {
    return null;
  }

  const activa = travesia?.pestanaEncendida ?? pestanaActiva(pathname);
  const enInicio = activa === 'inicio';
  // Durante la travesía las pestañas no navegan y las apagadas se atenúan.
  const propsTravesia = (encendida: boolean) =>
    bloqueada
      ? {
          'aria-disabled': true as const,
          tabIndex: -1,
          onClick: (e: { preventDefault: () => void }) => e.preventDefault(),
          'data-travesia-apagada': encendida ? undefined : true,
        }
      : {};
  const atenuar = (encendida: boolean): CSSProperties =>
    bloqueada && !encendida ? { opacity: OPACIDAD_TRAVESIA } : {};
  // Plan, la pantalla más usada, junto a Inicio y al alcance del pulgar.
  const izquierda: DatosPestana[] = [
    // «Movimientos» (H59, 6-oct): antes «Gastos». La ruta y el id siguen
    // siendo `gastos` para no romper enlaces.
    {
      id: 'gastos',
      nombre: t('Transactions'),
      ruta: '/gastos',
      icono: 'receipt',
    },
    { id: 'plan', nombre: t('Plan'), ruta: '/budget', icono: 'plan' },
  ];
  const derecha: DatosPestana[] = [
    {
      id: 'cuentas',
      nombre: t('Accounts'),
      ruta: '/accounts',
      icono: 'wallet',
    },
    { id: 'informes', nombre: t('Reports'), ruta: '/reports', icono: 'pie' },
  ];

  const pestana = (p: DatosPestana) => (
    <Link
      key={p.id}
      to={p.ruta}
      onClick={e => {
        e.preventDefault();
        irAPestana(p.ruta, navigate);
      }}
      aria-current={activa === p.id ? 'page' : undefined}
      data-testid={`pestana-${p.id}`}
      style={{ ...estiloPestana(activa === p.id), ...atenuar(activa === p.id) }}
      {...propsTravesia(activa === p.id)}
    >
      <Icono nombre={p.icono} size={21} />
      {p.nombre}
    </Link>
  );

  // En `body`, como el velo de la brújula: así la barra queda por encima del
  // velo (si no, el contexto de apilamiento de la app la dejaría debajo).
  return createPortal(
    <nav
      role="navigation"
      aria-label={t('Main')}
      data-barra-pestanas
      className={claseBarra}
      onPointerDown={(e: PointerEventReact<HTMLElement>) => {
        if (menuAbierto && toqueCierraMenu(e.currentTarget, e.target)) {
          setMenuAbierto(false);
        }
      }}
      style={{
        position: 'fixed',
        left: MARGEN,
        // La barra se acorta por la derecha para dejar sitio a la brújula.
        right: MARGEN + DIAMETRO_BRUJULA + HUECO_BRUJULA,
        bottom: `calc(${MARGEN}px + env(safe-area-inset-bottom, 0px))`,
        height: ALTO_BARRA,
        zIndex: 100,
      }}
    >
      {/* La parte visible de la barra va en su propia capa para atenuarla sin
          atenuar la brújula, que cuelga de la `nav` pero se ve suelta. */}
      <div
        data-testid="barra-fondo"
        style={{
          position: 'absolute',
          inset: 0,
          boxSizing: 'border-box',
          display: 'grid',
          // Columna central fija (Inicio + aire): el círculo queda centrado en
          // su hueco de la barra y no pisa Plan ni Cuentas.
          gridTemplateColumns: `repeat(2, minmax(0, 1fr)) ${INICIO + 6}px repeat(2, minmax(0, 1fr))`,
          alignItems: 'center',
          gap: 2,
          padding: '0 5px',
          borderRadius: radio.heroe,
          backgroundColor: color.surface,
          boxShadow: sombra.tarjeta,
          filter: menuAbierto ? FILTRO_BARRA_CON_MENU : 'none',
          transition: `filter ${movimiento.fondo}ms ease-out`,
        }}
      >
        {izquierda.map(pestana)}
        <span style={{ position: 'relative', alignSelf: 'stretch' }}>
          <Link
            to={RUTA_INICIO}
            onClick={e => {
              e.preventDefault();
              irAPestana(RUTA_INICIO, navigate);
            }}
            aria-current={enInicio ? 'page' : undefined}
            data-testid="pestana-inicio"
            {...propsTravesia(enInicio)}
            style={{
              ...styles.noTapHighlight,
              position: 'absolute',
              left: '50%',
              bottom: ALTO_BARRA + SOBRESALE - INICIO,
              width: INICIO,
              height: INICIO,
              marginLeft: -INICIO / 2,
              boxSizing: 'border-box',
              borderRadius: '50%',
              border: `4px solid ${color.bg}`,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              textDecoration: 'none',
              userSelect: 'none',
              fontSize: 10,
              fontWeight: 800,
              backgroundColor: enInicio ? color.accent : color.surface,
              color: enInicio ? color.accentInk : color.fg3,
              boxShadow: sombra.tarjeta,
              transition: 'none',
              ...atenuar(enInicio),
            }}
          >
            <Icono nombre="velero" size={26} strokeWidth={1.9} />
            <Trans>Home</Trans>
            {nuevos > 0 && (
              <span
                data-testid="insignia-revisar"
                aria-label={t('{{count}} new transactions', { count: nuevos })}
                style={{
                  position: 'absolute',
                  top: -3,
                  right: -5,
                  minWidth: 20,
                  height: 20,
                  padding: '0 4px',
                  boxSizing: 'border-box',
                  borderRadius: 10,
                  border: `2px solid ${color.bg}`,
                  backgroundColor: color.bad,
                  color: '#fff',
                  fontSize: 10,
                  fontWeight: 800,
                  lineHeight: '16px',
                  textAlign: 'center',
                }}
              >
                {nuevos > 99 ? '99+' : nuevos}
              </span>
            )}
          </Link>
        </span>
        {derecha.map(pestana)}
      </div>
      <MenuOpciones
        abierto={menuAbierto || brujulaForzada}
        onCambiarAbierto={setMenuAbierto}
        estiloBoton={{
          position: 'fixed',
          right: MARGEN,
          bottom: `calc(${MARGEN + (ALTO_BARRA - DIAMETRO_BRUJULA) / 2}px + env(safe-area-inset-bottom, 0px))`,
          // En la travesía la brújula no se toca hasta que se abre sola.
          ...(bloqueada && !brujulaForzada && { pointerEvents: 'none' }),
        }}
        desdeAbajo={ENCIMA_DE_LA_BARRA}
        claveRuta={pathname}
      />
      <style>
        {
          // Con el teclado de asignar abierto, la barra se retira del todo.
          'body[data-teclado-abierto] nav[data-barra-pestanas]{display:none!important}'
        }
      </style>
    </nav>,
    document.body,
  );
}
